/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Switch,
  Alert,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

const { width } = Dimensions.get('window');

// JOBENG Logo component using the app icon
const JobengLogo = () => (
  <View style={styles.logoContainer}>
    <Image
      source={require('../../../../assets/jobeng_icon.png')}
      style={styles.logoImage}
      resizeMode="contain"
    />
    <Text style={styles.logoText}>JOBENG</Text>
  </View>
);

const NotificationScheduleScreen = ({ navigation }) => {
  // Quiet Hours Settings
  const [quietHoursEnabled, setQuietHoursEnabled] = useState(false);
  const [quietStartTime, setQuietStartTime] = useState('22:00');
  const [quietEndTime, setQuietEndTime] = useState('08:00');

  // Weekend Settings
  const [weekendNotifications, setWeekendNotifications] = useState(true);
  const [weekendQuietHours, setWeekendQuietHours] = useState(false);
  const [weekendStartTime, setWeekendStartTime] = useState('23:00');
  const [weekendEndTime, setWeekendEndTime] = useState('09:00');

  // Working Hours Settings
  const [workingHoursOnly, setWorkingHoursOnly] = useState(false);
  const [workStartTime, setWorkStartTime] = useState('09:00');
  const [workEndTime, setWorkEndTime] = useState('17:00');

  // Day-specific Settings
  const [daySettings, setDaySettings] = useState({
    monday: { enabled: true, quietHours: false },
    tuesday: { enabled: true, quietHours: false },
    wednesday: { enabled: true, quietHours: false },
    thursday: { enabled: true, quietHours: false },
    friday: { enabled: true, quietHours: false },
    saturday: { enabled: true, quietHours: true },
    sunday: { enabled: true, quietHours: true },
  });

  // Frequency Settings
  const [instantNotifications, setInstantNotifications] = useState(true);
  const [batchNotifications, setBatchNotifications] = useState(false);
  const [batchInterval, setBatchInterval] = useState('1 hour');
  const [maxNotificationsPerHour, setMaxNotificationsPerHour] = useState(10);

  // Special Schedules
  const [holidayMode, setHolidayMode] = useState(false);
  const [vacationMode, setVacationMode] = useState(false);
  const [emergencyOverride, setEmergencyOverride] = useState(true);

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const timeSlots = [
    '00:00',
    '01:00',
    '02:00',
    '03:00',
    '04:00',
    '05:00',
    '06:00',
    '07:00',
    '08:00',
    '09:00',
    '10:00',
    '11:00',
    '12:00',
    '13:00',
    '14:00',
    '15:00',
    '16:00',
    '17:00',
    '18:00',
    '19:00',
    '20:00',
    '21:00',
    '22:00',
    '23:00',
  ];

  const batchIntervals = [
    '15 minutes',
    '30 minutes',
    '1 hour',
    '2 hours',
    '4 hours',
  ];
  const maxNotificationOptions = [5, 10, 15, 20, 25, 50];

  useEffect(() => {
    setHasUnsavedChanges(true);
  }, [
    quietHoursEnabled,
    quietStartTime,
    quietEndTime,
    weekendNotifications,
    weekendQuietHours,
    weekendStartTime,
    weekendEndTime,
    workingHoursOnly,
    workStartTime,
    workEndTime,
    daySettings,
    instantNotifications,
    batchNotifications,
    batchInterval,
    maxNotificationsPerHour,
    holidayMode,
    vacationMode,
    emergencyOverride,
  ]);

  const handleSaveSchedule = () => {
    Alert.alert(
      'Schedule Saved',
      'Your notification schedule has been updated successfully.',
      [{ text: 'OK' }]
    );
    setHasUnsavedChanges(false);
  };

  const handleTimeSelection = (currentTime, timeType, setter) => {
    Alert.alert(
      'Select Time',
      `Choose ${timeType} time:`,
      timeSlots
        .map(time => ({
          text: time,
          onPress: () => setter(time),
        }))
        .concat([{ text: 'Cancel', style: 'cancel' }])
    );
  };

  const handleBatchIntervalSelection = () => {
    Alert.alert(
      'Batch Interval',
      'How often should batched notifications be sent?',
      batchIntervals
        .map(interval => ({
          text: interval,
          onPress: () => setBatchInterval(interval),
        }))
        .concat([{ text: 'Cancel', style: 'cancel' }])
    );
  };

  const handleMaxNotificationsSelection = () => {
    Alert.alert(
      'Maximum Notifications',
      'Maximum notifications per hour:',
      maxNotificationOptions
        .map(count => ({
          text: count.toString(),
          onPress: () => setMaxNotificationsPerHour(count),
        }))
        .concat([{ text: 'Cancel', style: 'cancel' }])
    );
  };

  const toggleDaySetting = (day, setting) => {
    setDaySettings(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [setting]: !prev[day][setting],
      },
    }));
  };

  const renderSettingRow = (
    title,
    description,
    value,
    onValueChange,
    icon,
    iconColor = '#0e4e96'
  ) => (
    <View style={styles.settingRow}>
      <View style={styles.settingIcon}>
        <MaterialCommunityIcons name={icon} size={24} color={iconColor} />
      </View>
      <View style={styles.settingContent}>
        <Text style={styles.settingTitle}>{title}</Text>
        <Text style={styles.settingDescription}>{description}</Text>
      </View>
      <Switch
        value={value}
        onValueChange={onValueChange}
        trackColor={{ false: '#e1e1e1', true: '#0e4e96' }}
        thumbColor={value ? '#fff' : '#f4f3f4'}
      />
    </View>
  );

  const renderTimeSelector = (label, time, onPress) => (
    <TouchableOpacity style={styles.timeSelector} onPress={onPress}>
      <Text style={styles.timeSelectorLabel}>{label}</Text>
      <View style={styles.timeDisplay}>
        <Text style={styles.timeText}>{time}</Text>
        <MaterialCommunityIcons name="chevron-down" size={20} color="#666" />
      </View>
    </TouchableOpacity>
  );

  const renderDayCard = (day, dayName) => (
    <View key={day} style={styles.dayCard}>
      <View style={styles.dayHeader}>
        <Text style={styles.dayName}>{dayName}</Text>
        <Switch
          value={daySettings[day].enabled}
          onValueChange={() => toggleDaySetting(day, 'enabled')}
          trackColor={{ false: '#e1e1e1', true: '#0e4e96' }}
          thumbColor={daySettings[day].enabled ? '#fff' : '#f4f3f4'}
        />
      </View>
      <View style={styles.dayOption}>
        <Text style={styles.dayOptionText}>Quiet Hours</Text>
        <Switch
          value={daySettings[day].quietHours}
          onValueChange={() => toggleDaySetting(day, 'quietHours')}
          disabled={!daySettings[day].enabled}
          trackColor={{ false: '#e1e1e1', true: '#0e4e96' }}
          thumbColor={daySettings[day].quietHours ? '#fff' : '#f4f3f4'}
        />
      </View>
    </View>
  );

  const renderSectionHeader = (title, description) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {description && (
        <Text style={styles.sectionDescription}>{description}</Text>
      )}
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <JobengLogo />
        <TouchableOpacity
          onPress={() => navigation.navigate('NotificationSettingsCampaign')}
        >
          <MaterialCommunityIcons name="cog" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      {/* Title */}
      <View style={styles.titleContainer}>
        <Text style={styles.screenTitle}>Notification Schedule</Text>
        <Text style={styles.screenSubtitle}>
          Customize when you receive notifications
        </Text>
      </View>

      <ScrollView
        style={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {/* Quiet Hours */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Quiet Hours',
            'Pause non-urgent notifications during specified times'
          )}

          {renderSettingRow(
            'Enable Quiet Hours',
            'Automatically pause notifications during quiet hours',
            quietHoursEnabled,
            setQuietHoursEnabled,
            'sleep'
          )}

          {quietHoursEnabled && (
            <View style={styles.timeRangeContainer}>
              {renderTimeSelector('Start Time', quietStartTime, () =>
                handleTimeSelection(
                  quietStartTime,
                  'quiet start',
                  setQuietStartTime
                )
              )}
              {renderTimeSelector('End Time', quietEndTime, () =>
                handleTimeSelection(quietEndTime, 'quiet end', setQuietEndTime)
              )}
            </View>
          )}
        </View>

        {/* Weekend Settings */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Weekend Settings',
            'Configure notification behavior for weekends'
          )}

          {renderSettingRow(
            'Weekend Notifications',
            'Receive notifications on weekends',
            weekendNotifications,
            setWeekendNotifications,
            'calendar-weekend'
          )}

          {weekendNotifications &&
            renderSettingRow(
              'Weekend Quiet Hours',
              'Use different quiet hours for weekends',
              weekendQuietHours,
              setWeekendQuietHours,
              'calendar-clock'
            )}

          {weekendNotifications && weekendQuietHours && (
            <View style={styles.timeRangeContainer}>
              {renderTimeSelector('Weekend Start', weekendStartTime, () =>
                handleTimeSelection(
                  weekendStartTime,
                  'weekend start',
                  setWeekendStartTime
                )
              )}
              {renderTimeSelector('Weekend End', weekendEndTime, () =>
                handleTimeSelection(
                  weekendEndTime,
                  'weekend end',
                  setWeekendEndTime
                )
              )}
            </View>
          )}
        </View>

        {/* Working Hours */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Working Hours',
            'Limit notifications to business hours only'
          )}

          {renderSettingRow(
            'Working Hours Only',
            'Only receive notifications during working hours',
            workingHoursOnly,
            setWorkingHoursOnly,
            'briefcase-clock'
          )}

          {workingHoursOnly && (
            <View style={styles.timeRangeContainer}>
              {renderTimeSelector('Work Start', workStartTime, () =>
                handleTimeSelection(
                  workStartTime,
                  'work start',
                  setWorkStartTime
                )
              )}
              {renderTimeSelector('Work End', workEndTime, () =>
                handleTimeSelection(workEndTime, 'work end', setWorkEndTime)
              )}
            </View>
          )}
        </View>

        {/* Day-specific Settings */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Day-specific Settings',
            'Customize notifications for each day of the week'
          )}

          <View style={styles.daysGrid}>
            {renderDayCard('monday', 'Monday')}
            {renderDayCard('tuesday', 'Tuesday')}
            {renderDayCard('wednesday', 'Wednesday')}
            {renderDayCard('thursday', 'Thursday')}
            {renderDayCard('friday', 'Friday')}
            {renderDayCard('saturday', 'Saturday')}
            {renderDayCard('sunday', 'Sunday')}
          </View>
        </View>

        {/* Frequency Settings */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Frequency Settings',
            'Control how often you receive notifications'
          )}

          {renderSettingRow(
            'Instant Notifications',
            'Receive notifications immediately when events occur',
            instantNotifications,
            setInstantNotifications,
            'lightning-bolt'
          )}

          {renderSettingRow(
            'Batch Notifications',
            'Group notifications and send them at intervals',
            batchNotifications,
            setBatchNotifications,
            'package-variant'
          )}

          {batchNotifications && (
            <View style={styles.frequencyOptions}>
              <TouchableOpacity
                style={styles.optionSelector}
                onPress={handleBatchIntervalSelection}
              >
                <Text style={styles.optionLabel}>Batch Interval</Text>
                <View style={styles.optionValue}>
                  <Text style={styles.optionText}>{batchInterval}</Text>
                  <MaterialCommunityIcons
                    name="chevron-down"
                    size={20}
                    color="#666"
                  />
                </View>
              </TouchableOpacity>

              <TouchableOpacity
                style={styles.optionSelector}
                onPress={handleMaxNotificationsSelection}
              >
                <Text style={styles.optionLabel}>Max per Hour</Text>
                <View style={styles.optionValue}>
                  <Text style={styles.optionText}>
                    {maxNotificationsPerHour}
                  </Text>
                  <MaterialCommunityIcons
                    name="chevron-down"
                    size={20}
                    color="#666"
                  />
                </View>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {/* Special Modes */}
        <View style={styles.sectionContainer}>
          {renderSectionHeader(
            'Special Modes',
            'Temporary notification settings for special situations'
          )}

          {renderSettingRow(
            'Holiday Mode',
            'Reduce notifications during holidays',
            holidayMode,
            setHolidayMode,
            'party-popper'
          )}

          {renderSettingRow(
            'Vacation Mode',
            'Minimize notifications while on vacation',
            vacationMode,
            setVacationMode,
            'beach'
          )}

          {renderSettingRow(
            'Emergency Override',
            'Always receive critical security notifications',
            emergencyOverride,
            setEmergencyOverride,
            'alert-circle',
            '#dc3545'
          )}
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, !hasUnsavedChanges && styles.savedButton]}
          onPress={handleSaveSchedule}
        >
          <MaterialCommunityIcons
            name={hasUnsavedChanges ? 'content-save' : 'check'}
            size={24}
            color="#fff"
          />
          <Text style={styles.saveButtonText}>
            {hasUnsavedChanges ? 'Save Schedule' : 'Schedule Saved'}
          </Text>
        </TouchableOpacity>

        {/* Help Text */}
        <View style={styles.helpContainer}>
          <MaterialCommunityIcons
            name="information-outline"
            size={20}
            color="#666"
          />
          <Text style={styles.helpText}>
            Schedule changes take effect immediately. Emergency notifications
            will always override quiet hours and special modes.
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f7fa',
  },
  header: {
    backgroundColor: '#0e4e96',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoImage: {
    width: 30,
    height: 30,
    marginRight: 5,
  },
  logoText: {
    color: '#fff',
    fontWeight: 'bold',
  },
  titleContainer: {
    backgroundColor: '#fff',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e1e1e1',
  },
  screenTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  screenSubtitle: {
    fontSize: 16,
    color: '#666',
  },
  contentContainer: {
    flex: 1,
  },
  sectionContainer: {
    backgroundColor: '#fff',
    margin: 15,
    marginBottom: 0,
    borderRadius: 8,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  sectionHeader: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 5,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  settingIcon: {
    marginRight: 15,
  },
  settingContent: {
    flex: 1,
    marginRight: 15,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 18,
  },
  timeRangeContainer: {
    flexDirection: 'row',
    gap: 15,
    marginTop: 15,
  },
  timeSelector: {
    flex: 1,
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e1e1',
  },
  timeSelectorLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  timeDisplay: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  timeText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  daysGrid: {
    gap: 10,
  },
  dayCard: {
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e1e1',
  },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  dayName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  dayOption: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dayOptionText: {
    fontSize: 14,
    color: '#666',
  },
  frequencyOptions: {
    gap: 15,
    marginTop: 15,
  },
  optionSelector: {
    backgroundColor: '#f8f9fa',
    padding: 15,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e1e1e1',
  },
  optionLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 5,
  },
  optionValue: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  optionText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#333',
  },
  saveButton: {
    backgroundColor: '#28a745',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 15,
    margin: 15,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  savedButton: {
    backgroundColor: '#6c757d',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  helpContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#f8f9fa',
    padding: 15,
    margin: 15,
    marginTop: 0,
    borderRadius: 8,
  },
  helpText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
    marginLeft: 10,
    flex: 1,
  },
});

export default NotificationScheduleScreen;
